import type { UrlState } from "@/store/url/state";
import type { TracesInMemoryFilters } from "@/store/traces/state";
import { PerspectiveUrlConfig, ShareableUrl, TimeFilter } from "@/models/url";

/**
 * Retrieve the ShareableUrl object.
 * @param state - Vuex state for the url module.
 * @returns the ShareableUrl object.
 */
export function getUrlObject(state: UrlState): ShareableUrl {
  return state as ShareableUrl;
}

/**
 * Retrieve the string version of the ShareableUrl to use for the URL.
 * @param state - Vuex state for the url module.
 * @returns the url string.
 */
export function getUrlString(state: UrlState): string {
  const { timeFilter: _, ...stateToEncode } = state;

  const toEncode = filterNonEmptyValues(stateToEncode);

  if (Object.keys(toEncode).length === 0) {
    return getHumanTime(state);
  }
  const encoded = btoa(JSON.stringify(toEncode));
  return `${getHumanTime(state)}&s=${encoded}`;
}

/**
 * Retrieve the human friendly time range representation to display in url.
 * @param state - Vuex state for the url module.
 * @returns the human friendly time range representation.
 */
export function getHumanTime(state: UrlState): string {
  // do some magic to turn url.timeFilter to human friendly values
  const { start, end, lb } = { ...state.timeFilter };
  if (lb) {
    // lb has priority over start and end
    return `lb=${lb}`;
  } else {
    return `start=${start}&end=${end}`;
  }
}

/**
 * @param state - Vuex state for the url module.
 * @returns the TimeFilter object.
 */
export function getTimeFilter(state: UrlState): TimeFilter {
  return state.timeFilter;
}

/**
 * @param state - Vuex state for the url module.
 * @returns the filters value.
 */
export function getFilters(state: UrlState): any {
  return state.filters;
}

/**
 * @param state - Vuex state for the url module.
 * @returns the perspective configuration object.
 */
export function getPerspectiveConfiguration(
  state: UrlState
): PerspectiveUrlConfig | undefined {
  return state.perspectiveConfiguration;
}

/**
 * @param state - Vuex state for the url module.
 * @returns the insight panel value.
 */
export function getInsightPanel(state: UrlState): string | undefined {
  return state.insightPanel;
}

/**
 * @param state - Vuex state for the url module.
 * @returns the regex filter value.
 */
export function getRegexFilter(state: UrlState): string[] | string | undefined {
  return state.regexFilter;
}

/**
 * @param state - Vuex state for the url module.
 * @returns the severity number filter value.
 */
export function getSevNumFilter(state: UrlState): string[] | undefined {
  return state.sevNumFilter;
}

/**
 * @param state - Vuex state for the url module.
 * @returns the service name filter value.
 */
export function getServiceNameFilter(
  state: UrlState
): string[] | string | undefined {
  return state.serviceNameFilter;
}

/**
 *
 * @param state - Vuex state for the url module.
 * @returns the in memory table filters values.
 */
export function getInMemoryFilters(
  state: UrlState
): TracesInMemoryFilters | undefined {
  return state.inMemoryFilters;
}

/**
 * Filters the object to remove unpopulated entries. Recursively filters nested objects.
 * @param obj - The object to filter.
 * @returns A new object with only populated entries of original object.
 */
function filterNonEmptyValues(obj: Record<string, any>): Record<string, any> {
  const newObj: Record<string, any> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (Array.isArray(value)) {
      const filteredArray = value.filter(
        (item) => item !== undefined && item !== null
      );

      if (filteredArray.length > 0) {
        newObj[key] = filteredArray;
      }
    } else if (value !== undefined && value !== null) {
      newObj[key] =
        typeof value === "object" ? filterNonEmptyValues(value) : value;
    }
  }

  return newObj;
}
